import { useState } from "react";
import { Link } from "react-router-dom";
import {
  captureNote,
  deleteCapture,
  editCapture,
  fileCapture,
  type CaptureDestination,
} from "../actions";
import type { Capture } from "../data";
import { PersonPicker } from "./PersonPicker";
import { IcCheck, IcClock, IcPlus, IcTrash, IcUserPlus } from "./icons";

/**
 * The capture box, and the notes waiting in it.
 *
 * ## What was asked for
 *
 * "לגבי האינבוקס זה צריך להיות כמו שיש אינבוקס בטרלו, שכותבים שם דברים כדי שלא
 * ישכחו ואחר כך ממנינים אותם למקומם."
 *
 * The Inbox that existed is a queue of decisions the *system* could not make —
 * an unmatched payment, a possible duplicate, a contact from Google. Useful, and
 * not this. There was no way to write down "the Levis moved to Rothschild
 * street" anywhere in the product, which is the most ordinary thing anybody
 * wants from a notebook, let alone a CRM.
 *
 * ## Two halves, and the split is the design
 *
 * **Capturing** is free and instant. One field, no person, no date, no category,
 * no decision — asking for any of those at the moment of writing is what turns a
 * thought into a form and loses it. The note saves as a direct Firestore write,
 * so it lands the moment it is typed rather than after a cold start.
 *
 * **Filing** is where the decision lives, and it happens later, when reading
 * back down the list. Four destinations, which are the four things a note turns
 * out to be: a reminder with a date, a fact about somebody, something already
 * dealt with, or a mistake.
 *
 * The buttons are on every row rather than behind a menu. A three-dot menu on a
 * list of five notes is a tap to find out what the taps are.
 */
export function CaptureBox({ captures }: { captures: Capture[] }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /** Notes taken off the list by a tap, before the server has confirmed. */
  const [gone, setGone] = useState<Set<string>>(new Set());

  async function add() {
    const clean = text.trim();
    if (clean === "") return;
    setErr(null);
    setSaving(true);
    // Cleared first, on purpose. The note is about to exist and the next thought
    // is already arriving; making somebody wait to type it is the whole thing
    // this screen is meant to avoid.
    setText("");
    try {
      await captureNote(clean);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו לשמור. נסו שוב.");
      setText(clean); // handed back rather than lost
    } finally {
      setSaving(false);
    }
  }

  const visible = captures.filter((c) => !gone.has(c.id));

  return (
    <section className="section">
      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ padding: 12 }}>
          <textarea
            className="field-input"
            rows={2}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="מה לא לשכוח? כתבו עכשיו, סדרו אחר כך…"
            onKeyDown={(e) => {
              // Ctrl/⌘+Enter saves. A plain Enter has to stay a newline — a note
              // dictated in three sentences is one note.
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void add();
              }
            }}
          />
          <div className="btn-row" style={{ marginTop: 8 }}>
            <button
              className="btn btn-primary"
              onClick={() => void add()}
              disabled={saving || text.trim() === ""}
            >
              <IcPlus size={15} /> {saving ? "שומרים…" : "שמרו פתק"}
            </button>
            {text.trim() !== "" && (
              <button className="btn" onClick={() => setText("")} disabled={saving}>
                ניקוי
              </button>
            )}
          </div>
          {err && (
            <div className="notice error" style={{ marginTop: 8 }}>
              {err}
            </div>
          )}
        </div>
      </div>

      {visible.length > 0 && (
        <div className="card" style={{ marginTop: 10 }}>
          {visible.map((c, i) => (
            <CaptureRow
              key={c.id}
              capture={c}
              last={i === visible.length - 1}
              onGone={(id) => setGone((prev) => new Set(prev).add(id))}
              onFailed={(id) =>
                setGone((prev) => {
                  const next = new Set(prev);
                  next.delete(id);
                  return next;
                })
              }
            />
          ))}
        </div>
      )}
    </section>
  );
}

/** Noon tomorrow, as a date input value. The default for "remind me": soon
 *  enough to matter, not today, where it would be overdue by the evening. */
function tomorrow(): string {
  const d = new Date(Date.now() + 86_400_000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

type Panel = null | "task" | "person" | "edit";

function CaptureRow({
  capture,
  last,
  onGone,
  onFailed,
}: {
  capture: Capture;
  last: boolean;
  onGone: (id: string) => void;
  onFailed: (id: string) => void;
}) {
  const [panel, setPanel] = useState<Panel>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [due, setDue] = useState(tomorrow);
  const [personId, setPersonId] = useState<string | null>(null);
  const [personName, setPersonName] = useState("");
  const [draft, setDraft] = useState(capture.text ?? "");
  const [filedTo, setFiledTo] = useState<{ type: string; id: string } | null>(null);

  const text = capture.text ?? "";

  /**
   * Optimistic, like every other row on this screen.
   *
   * The note leaves the list on the tap and comes back with an error if the
   * server refuses. The product owner reported the alternative in plain terms:
   * "לוקח המון זמן לפעמים לדברים להשמר או להמחק. באפליקציה אחרת שבניתי הכל נשמר
   * תוך רגע".
   */
  async function file(to: CaptureDestination, extra: Record<string, unknown> = {}) {
    setBusy(true);
    setErr(null);
    if (to !== "task" && to !== "person") onGone(capture.id);
    try {
      const r = await fileCapture({ captureId: capture.id, to, ...extra });
      // A task or a note was created; say where it went rather than having the
      // row simply vanish. Somebody who files a reminder wants to know there is
      // now a reminder.
      if (to === "task" || to === "person") {
        setFiledTo(r.filedAs);
        setPanel(null);
        setTimeout(() => onGone(capture.id), 1400);
      }
    } catch (e) {
      onFailed(capture.id);
      setErr(e instanceof Error ? e.message : "לא הצלחנו לשמור. נסו שוב.");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setErr(null);
    onGone(capture.id);
    try {
      await deleteCapture(capture.id);
    } catch (e) {
      onFailed(capture.id);
      setErr(e instanceof Error ? e.message : "לא הצלחנו למחוק. נסו שוב.");
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit() {
    setBusy(true);
    setErr(null);
    try {
      await editCapture(capture.id, draft);
      setPanel(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו לשמור. נסו שוב.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ borderBottom: last ? undefined : "1px solid var(--line)" }}>
      {panel === "edit" ? (
        <div style={{ padding: 12 }}>
          <textarea
            className="field-input"
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            autoFocus
            disabled={busy}
          />
          <div className="btn-row" style={{ marginTop: 8 }}>
            <button className="btn btn-primary" onClick={() => void saveEdit()} disabled={busy}>
              {busy ? "שומרים…" : "שמרו"}
            </button>
            <button
              className="btn"
              onClick={() => {
                setDraft(text);
                setPanel(null);
              }}
              disabled={busy}
            >
              ביטול
            </button>
          </div>
        </div>
      ) : (
        <>
          <button
            className="card-body"
            style={{ width: "100%", textAlign: "start", paddingBottom: 6 }}
            onClick={() => {
              setDraft(text);
              setPanel("edit");
            }}
            aria-label="עריכת הפתק"
          >
            <p className="t-base" style={{ margin: 0, whiteSpace: "pre-wrap" }}>{text}</p>
          </button>

          {filedTo ? (
            <p className="faint" style={{ padding: "0 14px 12px", margin: 0 }}>
              <IcCheck size={13} />{" "}
              {filedTo.type === "task" ? (
                <>
                  הוספנו משימה. <Link to="/tasks">למשימות</Link>
                </>
              ) : (
                <>
                  הוספנו לכרטיס. <Link to={`/people/${filedTo.id}`}>לאיש הקשר</Link>
                </>
              )}
            </p>
          ) : (
            <div className="btn-row card-foot">
              <button
                className="btn"
                disabled={busy}
                onClick={() => setPanel(panel === "task" ? null : "task")}
              >
                <IcClock size={15} /> משימה
              </button>
              <button
                className="btn"
                disabled={busy}
                onClick={() => setPanel(panel === "person" ? null : "person")}
              >
                <IcUserPlus size={15} /> לאיש קשר
              </button>
              {/*
                "Done" and "delete" are kept apart even though they look the
                same here. One says the thing happened, the other says it should
                never have been written down, and the two answer different
                questions when somebody asks later what became of a note.
              */}
              <button className="btn" disabled={busy} onClick={() => void file("done")}>
                <IcCheck size={15} /> בוצע
              </button>
              <button className="btn" disabled={busy} onClick={() => void remove()}>
                <IcTrash size={15} /> <span className="danger">מחיקה</span>
              </button>
            </div>
          )}
        </>
      )}

      {panel === "task" && (
        <div className="card-foot">
          <div className="form-field">
            <span className="form-label">מתי להזכיר *</span>
            <input
              className="form-input"
              type="date"
              value={due}
              onChange={(e) => setDue(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="form-field">
            <span className="form-label">קשור למישהו</span>
            <PersonPicker
              personId={personId}
              personName={personName}
              disabled={busy}
              onPick={(person) => {
                setPersonId(person?.id ?? null);
                setPersonName(person?.name ?? "");
              }}
            />
          </div>
          <div className="btn-row">
            <button
              className="btn btn-primary"
              disabled={busy}
              onClick={() =>
                void file("task", {
                  // Midday local, so the reminder lands on the day that was
                  // picked whichever way the timezone rounds.
                  dueAt: new Date(`${due}T12:00:00`).toISOString(),
                  ...(personId ? { personId } : {}),
                })
              }
            >
              {busy ? "שומרים…" : "הוסיפו משימה"}
            </button>
            <button className="btn" disabled={busy} onClick={() => setPanel(null)}>
              ביטול
            </button>
          </div>
        </div>
      )}

      {panel === "person" && (
        <div className="card-foot">
          <div className="form-field">
            <span className="form-label">של מי זה *</span>
            <PersonPicker
              personId={personId}
              personName={personName}
              disabled={busy}
              placeholder="חיפוש לפי שם או טלפון…"
              onPick={(person) => {
                setPersonId(person?.id ?? null);
                setPersonName(person?.name ?? "");
              }}
            />
          </div>
          <p className="faint" style={{ marginTop: 0 }}>
            הפתק ייכנס לציר הזמן שלו כהערה. זה לא נחשב כשיחה איתו.
          </p>
          <div className="btn-row">
            <button
              className="btn btn-primary"
              disabled={busy || personId === null}
              onClick={() => void file("person", { personId })}
            >
              {busy ? "שומרים…" : "הוסיפו לכרטיס"}
            </button>
            <button className="btn" disabled={busy} onClick={() => setPanel(null)}>
              ביטול
            </button>
          </div>
        </div>
      )}

      {err && (
        <p className="faint danger" style={{ padding: "0 14px 12px", margin: 0 }}>
          {err}
        </p>
      )}
    </div>
  );
}
